import { useEffect, useState, useMemo } from 'react';
import api from '../../lib/api';
import { Tag } from '../../types';
import Button from '../../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import Input from '../../components/ui/Input';
import Textarea from '../../components/ui/Textarea';
import Select from '../../components/ui/Select';

type TagRow = Tag;

export default function AgencyTagsPage() {
  const [tags, setTags] = useState<TagRow[]>([]);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [parentId, setParentId] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editParentId, setEditParentId] = useState<string>('');

  const load = async () => {
    const res = await api.get('/tags');
    setTags(res.data);
  };

  useEffect(() => {
    load();
  }, []);

  const byId = useMemo(() => new Map(tags.map((t) => [t.id, t])), [tags]);

  const treeRoots = useMemo(() => {
    return tags.filter((t) => !t.parentId).sort((a, b) => a.name.localeCompare(b.name));
  }, [tags]);

  const childrenOf = (pid: string) =>
    tags.filter((t) => t.parentId === pid).sort((a, b) => a.name.localeCompare(b.name));

  const parentOptionsFor = (excludeId?: string) =>
    tags.filter((t) => t.id !== excludeId);

  const createTag = async () => {
    if (!name.trim()) return;
    await api.post('/tags', {
      name: name.trim(),
      description: description.trim() || undefined,
      parentId: parentId || undefined,
    });
    setName('');
    setDescription('');
    setParentId('');
    load();
  };

  const startEdit = (t: TagRow) => {
    setEditingId(t.id);
    setEditName(t.name);
    setEditDescription(t.description || '');
    setEditParentId(t.parentId || '');
  };

  const saveEdit = async () => {
    if (!editingId || !editName.trim()) return;
    await api.put(`/tags/${editingId}`, {
      name: editName.trim(),
      description: editDescription.trim() || undefined,
      parentId: editParentId || null,
    });
    setEditingId(null);
    load();
  };

  const removeTag = async (id: string) => {
    if (!confirm('Delete this tag? Child tags must be reassigned or deleted first.')) return;
    try {
      await api.delete(`/tags/${id}`);
      load();
    } catch (e: any) {
      alert(e.response?.data?.error || 'Delete failed (may be in use)');
    }
  };

  const renderBranch = (t: TagRow, depth: number) => {
    const kids = childrenOf(t.id);
    return (
      <div key={t.id} className={depth ? 'ml-6 border-l border-glass pl-3 mt-2' : ''}>
        <div className="flex flex-wrap items-center gap-2 py-1">
          {editingId === t.id ? (
            <>
              <Input className="w-40" value={editName} onChange={(e) => setEditName(e.target.value)} />
              <Textarea className="w-48 text-sm" rows={1} value={editDescription} onChange={(e) => setEditDescription(e.target.value)} />
              <Select value={editParentId} onChange={(e) => setEditParentId(e.target.value)}>
                <option value="">(root)</option>
                {parentOptionsFor(t.id).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </Select>
              <Button size="sm" onClick={saveEdit}>
                Save
              </Button>
              <Button size="sm" variant="outline" onClick={() => setEditingId(null)}>
                Cancel
              </Button>
            </>
          ) : (
            <>
              <span className="font-medium text-glass-text">{t.name}</span>
              {t.description && <span className="text-xs text-glass-text-2 truncate max-w-xs">{t.description}</span>}
              {t.parentId && (
                <span className="text-xs text-accent2">
                  under {byId.get(t.parentId)?.name || 'parent'}
                </span>
              )}
              <Button size="sm" variant="outline" onClick={() => startEdit(t)}>
                Edit
              </Button>
              <Button size="sm" variant="outline" onClick={() => removeTag(t.id)}>
                Delete
              </Button>
            </>
          )}
        </div>
        {kids.map((c) => renderBranch(c, depth + 1))}
      </div>
    );
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h1 className="text-[2.25rem] font-semibold tracking-tightest text-glass-text">Tags</h1>
        <p className="text-glass-text-2 text-sm mt-1">
          Hierarchical tags: KOLs tagged with a child still match parent filters in manual search.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>New tag</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Input placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} />
          <Textarea placeholder="Description (optional)" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
          <div>
            <label className="text-xs text-glass-text-2">Parent (optional)</label>
            <Select value={parentId} onChange={(e) => setParentId(e.target.value)}>
              <option value="">None — root tag</option>
              {tags.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </Select>
          </div>
          <Button onClick={createTag}>Create tag</Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Hierarchy</CardTitle>
        </CardHeader>
        <CardContent className="text-sm">
          {treeRoots.length === 0 && <p className="text-glass-text-2">No tags yet.</p>}
          {treeRoots.map((r) => renderBranch(r, 0))}
        </CardContent>
      </Card>
    </div>
  );
}
